/**
 * Platform-admin org-scope endpoints — port of v1
 * ``ando-frontend/app/api/organizations.ts`` (admin paths only).
 *
 * BE source of truth: ``ando-agent/src/api/v1/endpoints/platform_admin.py``.
 * The FastAPI router prefix is ``/platform-admin`` (mounted under the
 * ``admin_router`` which has no extra prefix), so every wire path here is
 * ``/platform-admin/organizations/{org_id}/...``.
 *
 * Every BE endpoint is gated by ``require_platform_admin`` server-side;
 * the FE additionally hides the entry point via ``useHasCapability("admin")``.
 *
 * Type contracts mirror v1 ``app/types/organizations.ts`` 1:1 — re-declared
 * here so the v2 monorepo stays self-contained (no cross-repo type drag).
 */

import type { DeploymentMode, OrgRole } from "../../domain/user.js";
import { apiClient } from "../ApiClient.js";
import { ApiError } from "../errors.js";

// Re-export so consumers that import from ``@ando/core`` only need one
// surface (the domain layer is the source-of-truth for these enums).
export type { DeploymentMode, OrgRole };

// ── Domain primitives (mirror v1 organizations.ts 1:1) ───────────────────

export type AssignmentMode = "all_members" | "per_user";
export type InviteStatus = "pending" | "accepted" | "expired" | "revoked";
export type AdminProductAccess = "workspace" | "engine" | "both";
export type ProvisionTier = "dedicated" | "isolated";
export type ProvisionStatus =
  | "requested"
  | "validating"
  | "provisioning_infra"
  | "provisioning_services"
  | "configuring"
  | "health_checking"
  | "active"
  | "failed"
  | "rolling_back"
  | "rolled_back"
  | "deprovisioning"
  | "deprovisioned";

export interface AdminOrganization {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  usage_multiplier: number;
  max_members: number;
  billing_email: string | null;
  deployment_mode: DeploymentMode;
  metadata: Record<string, unknown>;
  members_count?: number;
  created_at: string;
  updated_at: string;
}

export interface AdminOrganizationMember {
  product_access?: AdminProductAccess;
  id: string;
  org_id: string;
  user_id: string;
  role: OrgRole;
  is_active: boolean;
  invited_by: string | null;
  invited_at: string | null;
  joined_at: string | null;
  updated_at: string;
  email?: string;
  name?: string;
  usage_multiplier_override?: number | null;
  effective_storage_gb?: number;
  effective_cost_cap_eur?: number;
  effective_tier_label?: string;
}

export interface AdminOrganizationInvite {
  id: string;
  org_id: string;
  email: string;
  role: OrgRole;
  status: InviteStatus;
  expires_at: string;
  created_at: string;
  invited_by?: string | null;
  accepted_at?: string | null;
}

export interface AdminConnectorLicense {
  id: string;
  org_id: string;
  connector_key: string;
  is_enabled: boolean;
  licensed_seats: number;
  granted_by: string | null;
  granted_at: string | null;
  assignment_mode: AssignmentMode;
  /** Catalog metadata returned by the admin endpoint. */
  display_name?: string;
  category?: string;
  price_eur?: number;
}

export interface AdminMemberConnectorAccess {
  connector_key: string;
  display_name: string;
  category?: string;
  assignment_mode: string;
  is_assigned: boolean;
  can_toggle: boolean;
}

export interface AdminOrgUsageMemberBreakdown {
  user_id: string;
  email: string;
  name: string | null;
  estimated_cost_eur: number;
  storage_bytes: number;
}

export interface AdminOrgUsageSummary {
  org_id: string;
  org_name: string;
  usage_multiplier: number;
  member_count: number;
  cost_cap_eur: number;
  storage_limit_bytes: number;
  cost_used_eur: number;
  cost_used_pct: number;
  storage_used_bytes: number;
  storage_used_pct: number;
  members: AdminOrgUsageMemberBreakdown[];
}

export interface AdminOrgInvoice {
  id: string;
  number: string | null;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: "paid" | "open" | "draft" | "void";
  created: string;
  period_start: string | null;
  period_end: string | null;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
}

export interface AdminOrgBranding {
  brand_name: string | null;
  brand_primary_color: string | null;
  brand_accent_color: string | null;
  brand_logo_url: string | null;
  brand_logo_dark_url: string | null;
  brand_favicon_url: string | null;
}

export interface AdminTenantProvision {
  id: string;
  org_id: string;
  tier: ProvisionTier;
  status: ProvisionStatus;
  namespace: string | null;
  gcp_project_id: string | null;
  region: string;
  infra_outputs: Record<string, string>;
  current_step: string | null;
  retry_count: number;
  error_log: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface AdminIdpEntry {
  idp_id: string;
  name: string;
  type: string;
  source: "org" | "instance";
}

export interface AdminIdpConfig {
  configured: boolean;
  idps?: AdminIdpEntry[];
  reason?: string;
}

export interface AdminConfigureIdpRequest {
  name: string;
  idp_type: "OIDC" | "SAML" | "AZURE_AD" | "GOOGLE";
  client_id: string;
  client_secret: string;
  issuer?: string;
}

export interface AdminSetPasswordPolicyRequest {
  min_length: number;
  has_uppercase: boolean;
  has_lowercase: boolean;
  has_number: boolean;
  has_symbol: boolean;
}

export interface AdminSetMfaPolicyRequest {
  force_mfa: boolean;
}

export interface AdminUpdateOrgRequest {
  name?: string;
  usage_multiplier?: number;
  max_members?: number;
  billing_email?: string;
  is_active?: boolean;
}

export interface AdminAddMemberRequest {
  product_access?: AdminProductAccess;
  email: string;
  role?: OrgRole;
  /** Console access is a separate surface entitlement from the org role. */
  grants_console?: boolean;
}

export type AdminAddMemberResult =
  | { action: "added"; member: AdminOrganizationMember }
  | { action: "reactivated"; member: AdminOrganizationMember }
  | { action: "invited"; invite: AdminOrganizationInvite }
  | {
      // User already belongs to another org. Cross-org move is a dedicated
      // platform-admin migration (``migrate_user_to_org``), not an invite.
      action: "migration_required";
      current_org: { id: string; name: string };
    };

// ── Wire prefix ─────────────────────────────────────────────────────────

const BASE = "/platform-admin/organizations";

// ── Org lifecycle ───────────────────────────────────────────────────────

/** GET ``/platform-admin/organizations/{orgId}`` */
export async function getAdminOrg(orgId: string): Promise<AdminOrganization> {
  return apiClient.get<AdminOrganization>(`${BASE}/${orgId}`);
}

/** PUT ``/platform-admin/organizations/{orgId}`` */
export async function updateAdminOrg(
  orgId: string,
  body: AdminUpdateOrgRequest,
): Promise<AdminOrganization> {
  return apiClient.put<AdminOrganization>(`${BASE}/${orgId}`, body);
}

/** DELETE ``/platform-admin/organizations/{orgId}`` — soft deactivate. */
export async function deactivateAdminOrg(orgId: string): Promise<void> {
  await apiClient.delete<void>(`${BASE}/${orgId}`);
}

/** POST ``/platform-admin/organizations/{orgId}/reactivate`` */
export async function reactivateAdminOrg(
  orgId: string,
): Promise<{ status: "reactivated" | "already_active" }> {
  return apiClient.post<{ status: "reactivated" | "already_active" }>(
    `${BASE}/${orgId}/reactivate`,
    {},
  );
}

// ── Members ─────────────────────────────────────────────────────────────

/** GET ``/platform-admin/organizations/{orgId}/members`` */
export async function listAdminOrgMembers(orgId: string): Promise<AdminOrganizationMember[]> {
  return apiClient.get<AdminOrganizationMember[]>(`${BASE}/${orgId}/members`);
}

/** POST ``/platform-admin/organizations/{orgId}/members`` */
export async function addAdminOrgMember(
  orgId: string,
  body: AdminAddMemberRequest,
): Promise<AdminAddMemberResult> {
  return apiClient.post<AdminAddMemberResult>(`${BASE}/${orgId}/members`, body);
}

/** PUT ``/platform-admin/organizations/{orgId}/members/{userId}`` */
export async function updateAdminOrgMemberRole(
  orgId: string,
  userId: string,
  role: OrgRole,
): Promise<AdminOrganizationMember> {
  return apiClient.put<AdminOrganizationMember>(`${BASE}/${orgId}/members/${userId}`, { role });
}

export async function updateAdminOrgMemberProductAccess(
  orgId: string,
  userId: string,
  product_access: AdminProductAccess,
): Promise<{ user_id: string; product_access: AdminProductAccess }> {
  return apiClient.put(`${BASE}/${orgId}/members/${userId}/product-access`, { product_access });
}

/** DELETE ``/platform-admin/organizations/{orgId}/members/{userId}`` */
export async function removeAdminOrgMember(orgId: string, userId: string): Promise<void> {
  await apiClient.delete<void>(`${BASE}/${orgId}/members/${userId}`);
}

/** POST ``/platform-admin/organizations/{orgId}/members/{userId}/reactivate`` */
export async function reactivateAdminOrgMember(
  orgId: string,
  userId: string,
): Promise<{ action: "reactivated"; member: AdminOrganizationMember }> {
  return apiClient.post<{
    action: "reactivated";
    member: AdminOrganizationMember;
  }>(`${BASE}/${orgId}/members/${userId}/reactivate`, {});
}

export interface AdminUpdateMemberLimitsRequest {
  /** Per-user tier override. ``null`` = inherit from org. */
  usage_multiplier_override?: number | null;
  /** Per-user cost cap in EUR. ``null`` = inherit from org tier. */
  cost_cap_eur?: number | null;
  /** Per-user storage limit in bytes. ``null`` = inherit from org tier. */
  storage_limit_bytes?: number | null;
}

/**
 * PUT ``/platform-admin/organizations/{orgId}/members/{userId}/limits``
 *
 * The BE accepts ``usage_multiplier_override``, ``cost_cap_eur`` and
 * ``storage_limit_bytes`` independently (each optional, ``null`` clears
 * the override and inherits from org tier). Only fields present in
 * ``body`` are sent — uses ``Pydantic`` ``exclude_unset`` semantics on
 * the BE so omitted keys are left untouched.
 */
export async function updateAdminOrgMemberLimits(
  orgId: string,
  userId: string,
  body: AdminUpdateMemberLimitsRequest,
): Promise<AdminOrganizationMember> {
  return apiClient.put<AdminOrganizationMember>(`${BASE}/${orgId}/members/${userId}/limits`, body);
}

// ── Invites ─────────────────────────────────────────────────────────────

/** GET ``/platform-admin/organizations/{orgId}/invites`` */
export async function listAdminOrgInvites(orgId: string): Promise<AdminOrganizationInvite[]> {
  return apiClient.get<AdminOrganizationInvite[]>(`${BASE}/${orgId}/invites`);
}

/** DELETE ``/platform-admin/organizations/{orgId}/invites/{inviteId}`` */
export async function revokeAdminOrgInvite(orgId: string, inviteId: string): Promise<void> {
  await apiClient.delete<void>(`${BASE}/${orgId}/invites/${inviteId}`);
}

/** POST ``/platform-admin/organizations/{orgId}/invites/{inviteId}/resend`` */
export async function resendAdminOrgInvite(orgId: string, inviteId: string): Promise<void> {
  await apiClient.post<void>(`${BASE}/${orgId}/invites/${inviteId}/resend`, {});
}

// ── Connectors ──────────────────────────────────────────────────────────

/** GET ``/platform-admin/organizations/{orgId}/connectors`` */
export async function listAdminOrgConnectors(orgId: string): Promise<AdminConnectorLicense[]> {
  return apiClient.get<AdminConnectorLicense[]>(`${BASE}/${orgId}/connectors`);
}

/** PUT ``/platform-admin/organizations/{orgId}/connectors/{key}`` */
export async function setAdminOrgConnectorLicense(
  orgId: string,
  connectorKey: string,
  body: { is_enabled: boolean; licensed_seats?: number },
): Promise<AdminConnectorLicense> {
  return apiClient.put<AdminConnectorLicense>(`${BASE}/${orgId}/connectors/${connectorKey}`, body);
}

/** GET ``/platform-admin/organizations/{orgId}/members/{userId}/connectors`` */
export async function listAdminOrgMemberConnectors(
  orgId: string,
  userId: string,
): Promise<AdminMemberConnectorAccess[]> {
  return apiClient.get<AdminMemberConnectorAccess[]>(
    `${BASE}/${orgId}/members/${userId}/connectors`,
  );
}

/**
 * PUT ``/platform-admin/organizations/{orgId}/members/{userId}/connectors/{key}``
 */
export async function setAdminOrgMemberConnector(
  orgId: string,
  userId: string,
  connectorKey: string,
  assigned: boolean,
): Promise<void> {
  await apiClient.put<void>(`${BASE}/${orgId}/members/${userId}/connectors/${connectorKey}`, {
    assign: assigned,
  });
}

// ── Billing ─────────────────────────────────────────────────────────────

/** GET ``/platform-admin/organizations/{orgId}/billing/usage`` */
export async function getAdminOrgUsage(orgId: string): Promise<AdminOrgUsageSummary> {
  return apiClient.get<AdminOrgUsageSummary>(`${BASE}/${orgId}/billing/usage`);
}

/** GET ``/platform-admin/organizations/{orgId}/billing/invoices`` */
export async function getAdminOrgInvoices(orgId: string): Promise<AdminOrgInvoice[]> {
  return apiClient.get<AdminOrgInvoice[]>(`${BASE}/${orgId}/billing/invoices`);
}

/** A platform-usage invoice (one-off Stripe invoice for marked-up LLM cost). */
export interface PlatformInvoice {
  id: string;
  number: string | null;
  status: string | null;
  currency: string | null;
  amount_due: number | null;
  amount_paid: number | null;
  /** Billed period, ``YYYY-MM``. */
  period: string | null;
  /** Unix timestamp. */
  created: number | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

/** GET ``/platform-admin/organizations/{orgId}/billing/platform-invoices`` —
 * the org's platform-usage invoices (distinct from consumer-subscription). */
export async function getAdminOrgPlatformInvoices(orgId: string): Promise<PlatformInvoice[]> {
  return apiClient.get<PlatformInvoice[]>(`${BASE}/${orgId}/billing/platform-invoices`);
}

// ── Prepaid credits (per-tenant billing mode + balance) ──────────────────

export type BillingMode = "postpaid" | "prepaid";

export interface CreditGrant {
  id: string;
  amount_cents: number;
  kind: string;
  external_ref: string | null;
  note: string | null;
  created_at: string;
  expires_at: string | null;
}

export interface OrgCreditBalance {
  org_id: string;
  is_prepaid: boolean;
  granted_cents: number;
  consumed_cents: number;
  balance_cents: number;
  grants: CreditGrant[];
}

/** GET the org's prepaid posture + derived balance + recent grants. */
export async function getAdminOrgCreditBalance(orgId: string): Promise<OrgCreditBalance> {
  return apiClient.get<OrgCreditBalance>(`${BASE}/${orgId}/billing/credit-balance`);
}

/** PUT the org's billing mode (postpaid <-> prepaid). */
export async function setAdminOrgBillingMode(
  orgId: string,
  billingMode: BillingMode,
): Promise<{ org_id: string; billing_mode: BillingMode }> {
  return apiClient.put(`${BASE}/${orgId}/billing/mode`, { billing_mode: billingMode });
}

// ── Per-org billing configuration ────────────────────────────────────────
//
// How ONE org is billed, across the two commercial models:
//   · Ando engine    — per token (consumption + markup), collected on Stripe
//   · ando-platform  — per licence + seat, invoiced by the SRL
// Backed by ``GET/PUT /platform-admin/organizations/{id}/billing/config``.

/**
 * How the org's monthly usage invoice is collected.
 *
 * ``license_contract`` is the ando-platform model: the org is NOT billed per
 * token at all — it pays for licences and seats, invoiced separately by the
 * SRL — so no Stripe usage invoice is ever issued for it.
 */
export type OrgCollectionMethod = "charge_automatically" | "send_invoice" | "license_contract";

export interface OrgBillingConfig {
  org_id: string;
  /** null = inherits the platform default. */
  collection_method: OrgCollectionMethod | null;
  /** The override resolved against the default — what will actually happen. */
  effective_collection_method: OrgCollectionMethod;
  default_collection_method: OrgCollectionMethod;
  /** Negotiated €/seat for this org; null = use its tier's list price. */
  price_per_user_eur_override: number | null;
  /** ISO instant billing begins; null = no-pay indefinitely. */
  billing_starts_at: string | null;
  /** Derived server-side: billing_starts_at is unset or still in the future. */
  is_no_pay: boolean;
}

/**
 * Patch for {@link updateAdminOrgBillingConfig}.
 *
 * Absent key = leave unchanged. Key present with `null` = CLEAR it (inherit the
 * default / use the tier price / return to no-pay). The two are different, and
 * the backend distinguishes them by which fields were actually sent — so build
 * this object with only the keys you mean to touch.
 */
export interface AdminOrgBillingConfigPatch {
  collection_method?: OrgCollectionMethod | null;
  price_per_user_eur_override?: number | null;
  billing_starts_at?: string | null;
}

/** GET the org's billing configuration. */
export async function getAdminOrgBillingConfig(orgId: string): Promise<OrgBillingConfig> {
  return apiClient.get<OrgBillingConfig>(`${BASE}/${orgId}/billing/config`);
}

/** PUT a partial update of the org's billing configuration. */
export async function updateAdminOrgBillingConfig(
  orgId: string,
  patch: AdminOrgBillingConfigPatch,
): Promise<OrgBillingConfig> {
  return apiClient.put<OrgBillingConfig>(`${BASE}/${orgId}/billing/config`, patch);
}

/** POST a free/prepaid credit grant (USD) to the org. */
export async function grantAdminOrgCredit(
  orgId: string,
  amountUsd: number,
  note?: string,
): Promise<{ org_id: string; grant_id: string | null }> {
  return apiClient.post(`${BASE}/${orgId}/billing/credits`, {
    amount_usd: amountUsd,
    note,
  });
}

// ── Stripe portal / checkout ────────────────────────────────────────────
//
// NOTE (caller-tenant only): the BE currently exposes Stripe portal /
// checkout only as caller-scoped endpoints (``/billing/org/portal`` and
// ``/billing/org/checkout``), inferring the org from
// ``Depends(get_current_org_id)``. There is NO platform-admin variant
// that targets an arbitrary ``orgId``. Until a real
// ``/platform-admin/organizations/{orgId}/billing/portal`` ships, these
// wrappers open the Stripe surface for the CALLER's own org — which
// means the platform admin must be a member of the target org for the
// returned URL to be meaningful. The ``orgId`` argument is accepted to
// preserve the V2 admin-wrapper signature so adding the real BE later
// becomes a one-line BASE swap.
//
// Wire shape mirrors v1 ``app/types/organizations.ts``.

export interface AdminOrgPortalResponse {
  portal_url: string;
}

export interface AdminOrgCheckoutResponse {
  checkout_url: string;
}

/** POST ``/billing/org/portal`` — opens Stripe customer portal.
 *
 * The ``_orgId`` argument is accepted for forward-compat with the
 * eventual ``/platform-admin/organizations/{orgId}/billing/portal``
 * endpoint; today the BE infers the org from the caller's tenant. */
export async function createAdminOrgPortal(_orgId: string): Promise<AdminOrgPortalResponse> {
  return apiClient.post<AdminOrgPortalResponse>("/billing/org/portal", {});
}

/** POST ``/billing/org/checkout`` — opens Stripe checkout for subscription.
 *  See ``createAdminOrgPortal`` for the rationale on the ``_orgId``
 *  forward-compat argument. */
export async function createAdminOrgCheckout(_orgId: string): Promise<AdminOrgCheckoutResponse> {
  return apiClient.post<AdminOrgCheckoutResponse>("/billing/org/checkout", {});
}

// ── Branding ────────────────────────────────────────────────────────────

/** GET ``/platform-admin/organizations/{orgId}/branding`` */
export async function getAdminOrgBranding(orgId: string): Promise<AdminOrgBranding> {
  return apiClient.get<AdminOrgBranding>(`${BASE}/${orgId}/branding`);
}

/** PATCH ``/platform-admin/organizations/{orgId}/branding`` */
export async function updateAdminOrgBranding(
  orgId: string,
  body: Partial<AdminOrgBranding>,
): Promise<AdminOrgBranding> {
  return apiClient.patch<AdminOrgBranding>(`${BASE}/${orgId}/branding`, body);
}

/** DELETE ``/platform-admin/organizations/{orgId}/branding`` */
export async function resetAdminOrgBranding(orgId: string): Promise<void> {
  await apiClient.delete<void>(`${BASE}/${orgId}/branding`);
}

// ── Infrastructure / Provisioning ───────────────────────────────────────

/** GET ``/platform-admin/organizations/{orgId}/provision`` */
export async function getAdminOrgProvision(orgId: string): Promise<AdminTenantProvision | null> {
  // BE returns ``{}`` (or 404) when no provision exists; treat as ``null``
  // so consumers can gate UI cleanly without distinguishing wire shapes.
  try {
    const result = await apiClient.get<AdminTenantProvision | Record<string, never>>(
      `${BASE}/${orgId}/provision`,
    );
    if (!result || typeof result !== "object" || !("id" in result)) {
      return null;
    }
    return result as AdminTenantProvision;
  } catch {
    return null;
  }
}

/** POST ``/platform-admin/organizations/{orgId}/provision`` */
export async function requestAdminOrgProvision(
  orgId: string,
  body: { tier: ProvisionTier; region: string },
): Promise<AdminTenantProvision> {
  return apiClient.post<AdminTenantProvision>(`${BASE}/${orgId}/provision`, body);
}

/** POST ``/platform-admin/organizations/{orgId}/provision/execute`` */
export async function executeAdminOrgProvision(orgId: string): Promise<AdminTenantProvision> {
  return apiClient.post<AdminTenantProvision>(`${BASE}/${orgId}/provision/execute`, {});
}

/** POST ``/platform-admin/organizations/{orgId}/provision/retry`` */
export async function retryAdminOrgProvision(orgId: string): Promise<AdminTenantProvision> {
  return apiClient.post<AdminTenantProvision>(`${BASE}/${orgId}/provision/retry`, {});
}

/** DELETE ``/platform-admin/organizations/{orgId}/provision`` */
export async function deprovisionAdminOrg(orgId: string): Promise<void> {
  await apiClient.delete<void>(`${BASE}/${orgId}/provision`);
}

// ── SSO (IdP) ───────────────────────────────────────────────────────────

/** GET ``/platform-admin/organizations/{orgId}/idp`` */
export async function getAdminOrgIdp(orgId: string): Promise<AdminIdpConfig> {
  return apiClient.get<AdminIdpConfig>(`${BASE}/${orgId}/idp`);
}

/** POST ``/platform-admin/organizations/{orgId}/idp`` */
export async function configureAdminOrgIdp(
  orgId: string,
  body: AdminConfigureIdpRequest,
): Promise<{ idp_id: string }> {
  return apiClient.post<{ idp_id: string }>(`${BASE}/${orgId}/idp`, body);
}

/** DELETE ``/platform-admin/organizations/{orgId}/idp/{idpId}`` */
export async function removeAdminOrgIdp(orgId: string, idpId: string): Promise<void> {
  await apiClient.delete<void>(`${BASE}/${orgId}/idp/${idpId}`);
}

// ── Security policies ──────────────────────────────────────────────────

/** GET ``/platform-admin/organizations/{orgId}/mfa-policy`` */
export async function getAdminOrgMfaPolicy(orgId: string): Promise<AdminSetMfaPolicyRequest> {
  return apiClient.get<AdminSetMfaPolicyRequest>(`${BASE}/${orgId}/mfa-policy`);
}

/** PUT ``/platform-admin/organizations/{orgId}/mfa-policy`` */
export async function setAdminOrgMfaPolicy(
  orgId: string,
  body: AdminSetMfaPolicyRequest,
): Promise<void> {
  await apiClient.put<void>(`${BASE}/${orgId}/mfa-policy`, body);
}

/** GET ``/platform-admin/organizations/{orgId}/password-policy`` */
export async function getAdminOrgPasswordPolicy(
  orgId: string,
): Promise<AdminSetPasswordPolicyRequest> {
  return apiClient.get<AdminSetPasswordPolicyRequest>(`${BASE}/${orgId}/password-policy`);
}

/** PUT ``/platform-admin/organizations/{orgId}/password-policy`` */
export async function setAdminOrgPasswordPolicy(
  orgId: string,
  body: AdminSetPasswordPolicyRequest,
): Promise<void> {
  await apiClient.put<void>(`${BASE}/${orgId}/password-policy`, body);
}

// ── Listing (used by Admin Orgs surface + Migrate dialog picker) ─────────

/** Pagination-aware list response (BE returns {items,total,offset,limit}). */
export interface AdminOrgListResponse {
  items: AdminOrganization[];
  total: number;
  offset: number;
  limit: number;
}

export type AdminOrgActiveFilter = "active" | "inactive" | "all";

/**
 * GET ``/platform-admin/organizations`` — used by the Migrate User dialog
 * to power its target-org picker. The BE returns a paginated envelope; we
 * normalise to a single ``items`` array here because the picker pulls one
 * page large enough to cover any realistic admin tenant count.
 */
export async function listAdminOrgs(
  args: {
    offset?: number;
    limit?: number;
    activeFilter?: AdminOrgActiveFilter;
  } = {},
  signal?: AbortSignal,
): Promise<AdminOrgListResponse> {
  const params = new URLSearchParams({
    offset: String(args.offset ?? 0),
    limit: String(args.limit ?? 100),
  });
  const filter = args.activeFilter ?? "active";
  if (filter !== "all") {
    params.set("is_active", filter === "active" ? "true" : "false");
  }
  return apiClient.get<AdminOrgListResponse>(`${BASE}?${params.toString()}`, { signal });
}

// ── Hard-delete (saga + member auto-migration) ──────────────────────────

/**
 * Successful hard-delete: every member was migrated to the Default Tenant
 * and the org row was removed. ``failed_users`` is empty by construction.
 */
export interface AdminHardDeleteOrgDeleted {
  status: "deleted";
  org_id: string;
  deleted: true;
  migrated_users: { user_id: string; audit_id: string }[];
  failed_users: never[];
}

/**
 * Partial-migration outcome: at least one member could NOT be relocated,
 * so the BE rolled back the org delete (HTTP 409). The body is surfaced
 * to the caller as a normal return value so the dialog can render a
 * structured "fix and retry" view without a try/catch.
 */
export interface AdminHardDeleteOrgPartial {
  status: "partial_migration";
  org_id: string;
  deleted: false;
  migrated_users: { user_id: string; audit_id: string }[];
  failed_users: { user_id: string; error: string }[];
}

export type AdminHardDeleteOrgResult = AdminHardDeleteOrgDeleted | AdminHardDeleteOrgPartial;

/**
 * DELETE ``/platform-admin/organizations/{orgId}/hard`` — irreversible
 * cascading delete with auto-migration to the Default Tenant. The body
 * carries an explicit ``confirm_migrate_users`` flag so the BE refuses
 * accidental calls.
 *
 * The 409 ``partial_migration`` response is converted to a non-throwing
 * union branch (matches v1 ``hardDeleteOrganization`` semantics). Every
 * other error keeps propagating.
 */
export async function hardDeleteAdminOrg(orgId: string): Promise<AdminHardDeleteOrgResult> {
  try {
    return await apiClient.delete<AdminHardDeleteOrgResult>(`${BASE}/${orgId}/hard`, {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ confirm_migrate_users: true }),
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      const body = err.data as AdminHardDeleteOrgResult | undefined;
      if (body && body.status === "partial_migration") return body;
    }
    throw err;
  }
}

// ── User migration (cross-tenant) ───────────────────────────────────────

export interface AdminMigrateUserRequest {
  /** Destination org. */
  target_org_id: string;
  /**
   * When true (default) the per-row data follows the user atomically.
   * Setting this to false leaves the source-org data behind — used very
   * rarely, only when the admin is intentionally splitting a user from
   * their previous tenant's data.
   */
  migrate_data?: boolean;
}

export interface AdminMigrateUserResponse {
  user_id: string;
  source_org_id: string | null;
  target_org_id: string;
  migrated_data: boolean;
  table_counts: Record<string, number> | null;
  audit_id: string;
}

/**
 * POST ``/platform-admin/users/{userId}/migrate`` — single legitimate
 * cross-tenant transition path. Note the wire prefix is
 * ``/platform-admin/users``, NOT ``${BASE}`` (which is org-scoped).
 *
 * BE error map: 404 user_not_found / target_org_not_found,
 *               409 already_in_target_org / owner_cannot_migrate.
 */
export async function migrateAdminUser(
  userId: string,
  body: AdminMigrateUserRequest,
): Promise<AdminMigrateUserResponse> {
  return apiClient.post<AdminMigrateUserResponse>(`/platform-admin/users/${userId}/migrate`, body);
}
